import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faWindowMaximize,
  faWindowMinimize,
  faArrowLeft,
  faArrowRight,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

const PREVIEW_BRIDGE_SOURCE = "preview-bootstrap";
const COMMON_PORTS = ["5000", "5001"];

function parsePreviewAddress(addr, fallbackPort) {
  const cleaned = (addr || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^\/+/, "");
  const withHost = cleaned.match(/^(?:localhost|127\.0\.0\.1):(\d+)(?:\/(.*))?$/i);
  if (withHost) {
    return {
      port: withHost[1],
      path: (withHost[2] || "").replace(/^\/+/, "").trim(),
    };
  }
  const portOnly = cleaned.match(/^(\d{2,5})$/);
  if (portOnly) {
    return { port: portOnly[1], path: "" };
  }
  return {
    port: fallbackPort || "5000",
    path: cleaned.replace(/^\/+/, "").trim(),
  };
}

function AppPreviewModal({
  show,
  onHide,
  containerStatus,
  previewPath,
  setPreviewPath,
  previewPort,
  setPreviewPort,
  previewMinimized,
  setPreviewMinimized,
}) {
  const containerUrl = containerStatus?.containerUrl;
  const iframeRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [address, setAddress] = useState("");
  const [portInput, setPortInput] = useState(previewPort || "5000");
  const isMaximized = previewMinimized;
  const activePort = (previewPort || "5000").replace(/\D/g, "") || "5000";

  const base = containerUrl
    ? containerUrl.replace(/\/?$/, "") + "/proxy/" + activePort
    : "";
  const pathPart = (previewPath || "").replace(/^\//, "").trim();
  const fullUrl = base ? (pathPart ? base + "/" + pathPart : base + "/") : "";
  const localhostUrl = `localhost:${activePort}${pathPart ? "/" + pathPart : "/"}`;

  const publicUrl =
    typeof process !== "undefined" && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : "";
  const bootstrapUrl = fullUrl
    ? `${window.location.origin}${publicUrl.replace(
        /\/?$/,
        "",
      )}/preview-bootstrap.html?target=${encodeURIComponent(fullUrl)}`
    : "";

  useEffect(() => {
    setPortInput(activePort);
  }, [activePort]);

  useEffect(() => {
    if (!bootstrapUrl) return;
    setHistory([bootstrapUrl]);
    setHistoryIndex(0);
    setAddress(localhostUrl);
  }, [bootstrapUrl, localhostUrl]);

  const buildBootstrap = (port, path) => {
    const clean = (path || "").replace(/^\/+/, "");
    const target =
      containerUrl.replace(/\/?$/, "") +
      "/proxy/" +
      port +
      (clean ? "/" + clean : "/");
    return (
      `${window.location.origin}${publicUrl.replace(/\/?$/, "")}/preview-bootstrap.html?target=` +
      encodeURIComponent(target)
    );
  };

  const navigateTo = (port, path) => {
    const cleanPort = (port || "5000").replace(/\D/g, "") || "5000";
    const clean = (path || "").replace(/^\/+/, "");
    setPreviewPort(cleanPort);
    setPortInput(cleanPort);
    setPreviewPath(clean ? "/" + clean : "");

    const newAddress = `localhost:${cleanPort}${clean ? "/" + clean : "/"}`;
    const newBootstrap = buildBootstrap(cleanPort, clean);
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newBootstrap);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setAddress(newAddress);
  };

  const applyAddressBar = () => {
    const parsed = parsePreviewAddress(address, activePort);
    navigateTo(parsed.port, parsed.path);
  };

  const applyPort = (nextPort) => {
    const cleanPort = (nextPort || "5000").replace(/\D/g, "") || "5000";
    navigateTo(cleanPort, pathPart);
  };

  const goBack = () => {
    if (historyIndex > 0) setHistoryIndex(historyIndex - 1);
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) setHistoryIndex(historyIndex + 1);
  };

  const refresh = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.location.reload();
    }
  };

  useEffect(() => {
    if (!show) return;

    const onMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.source !== PREVIEW_BRIDGE_SOURCE) return;

      if (data.type === "STACKBLITZ_BRIDGE_NAVIGATION" || data.type === "PREVIEW_NAVIGATION") {
        if (typeof data.url === "string") {
          try {
            const u = new URL(data.url, "http://x");
            const pathname = u.pathname || "";
            const proxyMatch = pathname.match(/\/proxy\/(\d+)\/?(.*)/);
            if (proxyMatch) {
              const portFromUrl = proxyMatch[1];
              const path = (proxyMatch[2] || "").replace(/^\/+/, "").trim();
              if (portFromUrl && portFromUrl !== activePort) {
                setPreviewPort(portFromUrl);
                setPortInput(portFromUrl);
              }
              setAddress(`localhost:${portFromUrl || activePort}${path ? "/" + path : "/"}`);
              setPreviewPath(path ? "/" + path : "");
            } else {
              const path = pathname.replace(/^\/+/, "").trim();
              setAddress(`localhost:${activePort}${path ? "/" + path : "/"}`);
              setPreviewPath(path ? "/" + path : "");
            }
          } catch (error) {}
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [show, previewPort, activePort, setPreviewPath, setPreviewPort]);

  const currentUrl = historyIndex >= 0 ? history[historyIndex] : "";

  return (
    <>
      <style>{`
        .preview-modal {
          z-index: 1300 !important;
        }
        .preview-modal-backdrop {
          z-index: 1290 !important;
        }
        .preview-modal.preview-70 .modal-dialog {
          width: 70vw;
          max-width: 70vw;
          height: 90vh;
          margin: 5vh auto;
        }
        .preview-modal.preview-70 .modal-content {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .preview-modal.modal-fullscreen .modal-dialog {
          max-width: 100%;
          height: 100%;
          margin: 0;
        }
        .preview-modal.modal-fullscreen .modal-content {
          height: 100%;
        }
        .preview-modal .modal-body {
          flex: 1;
          min-height: 0;
        }
        .preview-modal .preview-toolbar {
          flex-shrink: 0;
          min-height: 44px;
        }
      `}</style>
      <Modal
        show={show}
        onHide={onHide}
        fullscreen={isMaximized ? true : undefined}
        className={isMaximized ? "preview-modal modal-fullscreen" : "preview-modal preview-70"}
        backdropClassName="preview-modal-backdrop"
        contentClassName="border-0 shadow d-flex flex-column h-100"
      >
        <div className="preview-toolbar d-flex align-items-center gap-2 px-2 py-1 border-bottom bg-light flex-wrap">
          <button type="button" className="btn btn-sm btn-light" onClick={goBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button type="button" className="btn btn-sm btn-light" onClick={goForward}>
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button type="button" className="btn btn-sm btn-light" onClick={refresh}>
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
          <div className="d-flex align-items-center gap-1" title="App port">
            <span className="small text-muted text-nowrap">Port</span>
            <input
              type="text"
              inputMode="numeric"
              className="form-control form-control-sm"
              style={{ width: 72 }}
              value={portInput}
              list="cc-preview-common-ports"
              onChange={(e) => setPortInput(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
              onBlur={() => applyPort(portInput)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyPort(portInput);
                }
              }}
              aria-label="Preview port"
            />
            <datalist id="cc-preview-common-ports">
              {COMMON_PORTS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
          <input
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: 160 }}
            value={address}
            placeholder="localhost:5000/ or localhost:5001/"
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyAddressBar();
              }
            }}
            title="Address (include port to switch, e.g. localhost:5001/)"
          />
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setPreviewMinimized((m) => !m)}
          >
            <FontAwesomeIcon icon={isMaximized ? faWindowMinimize : faWindowMaximize} />
          </button>
          <button type="button" className="btn btn-sm btn-light" onClick={onHide}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <Modal.Body className="p-0 flex-grow-1 overflow-hidden">
          {currentUrl ? (
            <iframe
              ref={iframeRef}
              title="App Preview"
              src={currentUrl}
              className="w-100 h-100 border-0"
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center text-muted h-100">
              Start your app in the terminal, then set the port (e.g. 5000, 500)
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AppPreviewModal;
